import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import HistorialPanel from './HistorialPanel';
import GeocercaManager from './GeocercaManager';
import { cargarGoogleMaps, crearCapaBase } from './googleMapsTiles';

const TILE_DARK = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const TILE_OSM  = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_SAT  = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const TILE_URLS = { dark: TILE_DARK, osm: TILE_OSM, sat: TILE_SAT };

function iconoObjetivo(nombre_clave, velocidad) {
  const letra = (nombre_clave || 'O').charAt(0);
  const color = velocidad > 0 ? '#f87171' : '#fbbf24';
  return window.L.divIcon({
    html: `<div style="
      width:28px; height:28px; border-radius:50%;
      background:${color}; color:#04080f;
      display:flex; align-items:center; justify-content:center;
      font-weight:900; font-size:12px;
      box-shadow: 0 0 8px ${color};
      border: 2px solid #04080f;
    ">${letra}</div>`,
    iconSize: [28, 28], iconAnchor: [14, 14], className: '',
  });
}

export default function MapaObjetivos({ usuario, onUsuarioActualizado }) {
  const mapRef      = useRef(null);
  const mapaInst    = useRef(null);
  const marcadores  = useRef({});
  const rutaLines   = useRef({});
  const tileRef     = useRef(null);
  const esRefs      = useRef({});
  const omsRef      = useRef(null);

  const [tipoMapa,     setTipoMapa]     = useState('dark');
  const [dispositivos, setDispositivos] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [modalNuevo,   setModalNuevo]   = useState(false);
  const [formDev,      setFormDev]      = useState({ caso_ref:'', nombre_clave:'', imei:'', notas:'' });
  const [guardando,    setGuardando]    = useState(false);
  const [error,        setError]        = useState('');
  const [custodia,     setCustodia]     = useState(null);
  const [mostrarRuta,  setMostrarRuta]  = useState(false);
  const [histObj,      setHistObj]      = useState(null);
  const [geoObj,       setGeoObj]       = useState(null);

  // Inicializar mapa
  useEffect(() => {
    if (!mapRef.current || mapaInst.current) return;
    let cancelado = false;
    cargarGoogleMaps().finally(() => {
      if (cancelado || !mapRef.current || mapaInst.current) return;
      const L = window.L;
      const mapa = L.map(mapRef.current, {
        center: [23.634501, -102.552784],
        zoom: 5,
      });
      tileRef.current = crearCapaBase('dark', L, TILE_URLS);
      tileRef.current.addTo(mapa);
      mapaInst.current = mapa;
      omsRef.current = new window.OverlappingMarkerSpiderfier(mapa, {
        nearbyDistance: 15, circleSpiralSwitchover: 9,
        circleFootSeparation: 40, spiralFootSeparation: 28,
        spiralLengthStart: 11, spiralLengthFactor: 4,
        keepSpiderfied: true, legWeight: 1.5,
        legColors: { usual: 'rgba(251,191,36,0.4)', highlighted: '#fbbf24' },
      });
    });
    return () => { cancelado = true; };
  }, []);

  useEffect(() => {
    if (!mapaInst.current) return;
    const L = window.L;
    if (tileRef.current) mapaInst.current.removeLayer(tileRef.current);
    tileRef.current = crearCapaBase(tipoMapa, L, TILE_URLS);
    tileRef.current.addTo(mapaInst.current);
    tileRef.current.bringToBack();
  }, [tipoMapa]);

  const cargarDispositivos = useCallback(() => {
    axios.get('/api/v1/objetivos').then(r => setDispositivos(r.data)).catch(() => {});
  }, []);

  useEffect(() => { cargarDispositivos(); }, [cargarDispositivos]);

  // SSE por dispositivo activo
  useEffect(() => {
    // Cerrar SSE anteriores
    Object.values(esRefs.current).forEach(es => es.close());
    esRefs.current = {};

    dispositivos.filter(d => d.activo).forEach(d => {
      const es = new EventSource(`/stream/objetivo/${d.id}`, { withCredentials: true });
      esRefs.current[d.id] = es;
      es.onmessage = (e) => {
        const pos = JSON.parse(e.data);
        if (pos && pos.lat) actualizarMarcador(d, pos);
      };
    });

    return () => Object.values(esRefs.current).forEach(es => es.close());
  }, [dispositivos]);

  const actualizarMarcador = (d, pos) => {
    if (!mapaInst.current) return;
    const L = window.L;
    const key = d.imei;
    const icon = iconoObjetivo(d.nombre_clave, pos.velocidad);

    const popup = `
      <div style="background:#0b132b;color:#e0e1dd;padding:10px;border-radius:8px;min-width:180px;font-size:13px">
        <div style="font-weight:700;color:#fbbf24;margin-bottom:6px">🎯 ${d.nombre_clave}</div>
        <div>📁 Caso: ${d.caso_ref}</div>
        <div>🏎️ ${pos.velocidad || 0} km/h · Rumbo: ${pos.rumbo || 0}°</div>
        <div>🔋 ${pos.bateria_pct || 0}%</div>
        <div style="color:#8892a4;font-size:11px;margin-top:6px">${pos.timestamp || ''}</div>
        <div style="color:#8892a4;font-size:10px">🔐 DATOS COMPARTIMENTADOS</div>
      </div>`;

    if (marcadores.current[key]) {
      marcadores.current[key].setLatLng([pos.lat, pos.lng]).setIcon(icon).setPopupContent(popup);
    } else {
      const m = L.marker([pos.lat, pos.lng], { icon }).bindPopup(popup);
      if (omsRef.current) omsRef.current.addMarker(m);
      m.addTo(mapaInst.current);
      marcadores.current[key] = m;
    }
  };

  const verRuta = async (d) => {
    setSeleccionado(d);
    setMostrarRuta(true);
    const L = window.L;
    try {
      const r = await axios.get(`/api/v1/objetivos/${d.id}/ruta`, { params: { limite: 200 } });
      const coords = r.data.map(p => [p.lat, p.lng]);
      if (rutaLines.current[d.id]) mapaInst.current.removeLayer(rutaLines.current[d.id]);
      if (coords.length > 1) {
        const linea = L.polyline(coords, { color:'#fbbf24', weight:2, opacity:.7, dashArray:'6,4' });
        linea.addTo(mapaInst.current);
        rutaLines.current[d.id] = linea;
        mapaInst.current.fitBounds(linea.getBounds(), { padding: [40,40] });
      }
    } catch (_) {}
  };

  const verCustodia = async (d) => {
    try {
      const r = await axios.get(`/api/v1/objetivos/${d.id}/custodia`);
      setCustodia(r.data);
    } catch (_) {}
  };

  const limpiarRuta = () => {
    Object.values(rutaLines.current).forEach(l => { if (mapaInst.current) mapaInst.current.removeLayer(l); });
    rutaLines.current = {};
    setMostrarRuta(false);
    setSeleccionado(null);
  };

  const guardarDispositivo = async () => {
    setGuardando(true); setError('');
    try {
      await axios.post('/api/v1/objetivos', formDev);
      setModalNuevo(false);
      setFormDev({ caso_ref:'', nombre_clave:'', imei:'', notas:'' });
      cargarDispositivos();
      // Refresca el badge de créditos del header de inmediato — antes solo se
      // actualizaba con el heartbeat de 2 min, mostrando el saldo desactualizado
      // justo después de gastar un token.
      if (onUsuarioActualizado) {
        axios.get('/api/v1/auth/me').then(r => onUsuarioActualizado(r.data)).catch(() => {});
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al registrar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ display:'flex', height:'100%', position:'relative' }}>
      {/* Mapa */}
      <div ref={mapRef} style={{ flex:1, height:'100%' }} />

      {/* Controles tile */}
      <div style={{ position:'absolute', top:10, left:10, zIndex:1000, display:'flex', gap:6 }}>
        {['dark','osm','sat'].map(t => (
          <button key={t} onClick={() => setTipoMapa(t)} style={{
            padding:'5px 10px', border:'1px solid #1c2541', borderRadius:6,
            cursor:'pointer', fontSize:12, fontWeight:600,
            background: tipoMapa===t ? '#fbbf24' : 'rgba(11,19,43,.9)',
            color: tipoMapa===t ? '#04080f' : '#8892a4',
          }}>
            {t==='dark' ? '🌙 Noche' : t==='osm' ? '🗺️ OSM' : '🛰️ Satélite'}
          </button>
        ))}
        {mostrarRuta && (
          <button onClick={limpiarRuta} style={{
            padding:'5px 10px', border:'1px solid #f87171',
            borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600,
            background:'rgba(69,10,10,.9)', color:'#f87171',
          }}>✕ Limpiar ruta</button>
        )}
      </div>

      {/* Panel historial objetivo */}
      {histObj && (
        <div style={{ width:340, borderLeft:'1px solid #1c2541', flexShrink:0 }}>
          <HistorialPanel tipo="objetivo" entidadId={histObj.id} entidad={histObj}
            mapaInst={mapaInst} esAdmin={usuario.es_admin}
            onClose={() => setHistObj(null)} />
        </div>
      )}

      {/* Panel geocercas objetivo */}
      {geoObj && !histObj && (
        <div style={{ width:340, borderLeft:'1px solid #1c2541', flexShrink:0 }}>
          <GeocercaManager tipo="objetivo" entidadId={geoObj.id}
            mapaInst={mapaInst} esAdmin={usuario.es_admin}
            onClose={() => setGeoObj(null)} />
        </div>
      )}

      {/* Panel lateral */}
      {!histObj && !geoObj && <div style={{
        width:320, background:'#0b132b', borderLeft:'1px solid #1c2541',
        display:'flex', flexDirection:'column'
      }}>
        <div style={{ padding:'12px 16px', borderBottom:'1px solid #1c2541',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontWeight:700, color:'#fbbf24' }}>
            🎯 Objetivos ({dispositivos.length})
          </span>
          <button className="btn" onClick={() => setModalNuevo(true)} style={{
            fontSize:12, padding:'4px 10px',
            background:'#fbbf24', color:'#04080f',
          }}>
            + Objetivo
          </button>
        </div>

        <div style={{ flex:1, overflow:'auto', padding:8 }}>
          {dispositivos.length === 0 ? (
            <div style={{ color:'#8892a4', textAlign:'center', padding:24, fontSize:13 }}>
              Sin dispositivos registrados.
              {!usuario.es_admin && <><br/><span style={{fontSize:11}}>Se requiere 1 crédito GPS.</span></>}
            </div>
          ) : dispositivos.map(d => (
            <div key={d.id} style={{
              background:'#04080f', border:`1px solid ${seleccionado?.id===d.id ? '#fbbf24' : '#1c2541'}`,
              borderRadius:8, padding:10, marginBottom:6,
            }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ fontWeight:600, color:'#fbbf24' }}>🎯 {d.nombre_clave}</span>
                <span style={{
                  background: d.activo ? '#052e16' : '#1c2541',
                  color: d.activo ? '#4ade80' : '#8892a4',
                  padding:'1px 6px', borderRadius:12, fontSize:10, fontWeight:700
                }}>
                  {d.activo ? '● ACTIVO' : '○ INACTIVO'}
                </span>
              </div>
              <div style={{ color:'#8892a4', fontSize:11 }}>📁 {d.caso_ref}</div>
              <div style={{ color:'#8892a4', fontSize:11 }}>👤 {d.analista_nombre || '—'}</div>
              <div style={{ display:'flex', gap:6, marginTop:8 }}>
                <button onClick={() => verRuta(d)} style={{
                  flex:1, padding:'4px 0', background:'rgba(251,191,36,.15)',
                  border:'1px solid #fbbf24', color:'#fbbf24',
                  borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600
                }}>📍 Ruta</button>
                <button onClick={() => setHistObj(d)} style={{
                  flex:1, padding:'4px 0', background:'rgba(0,200,255,.1)',
                  border:'1px solid #00c8ff', color:'#00c8ff',
                  borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600
                }}>📊 Historial</button>
                <button onClick={() => setGeoObj(d)} style={{
                  flex:1, padding:'4px 0', background:'rgba(255,102,0,.15)',
                  border:'1px solid #ff6600', color:'#ff6600',
                  borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600
                }}>🔲 Geocercas</button>
                <button onClick={() => verCustodia(d)} style={{
                  flex:1, padding:'4px 0', background:'rgba(0,200,255,.1)',
                  border:'1px solid #00c8ff', color:'#00c8ff',
                  borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600
                }}>🔐 Custodia</button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding:'8px 16px', borderTop:'1px solid #1c2541',
                      color:'#8892a4', fontSize:11 }}>
          🔒 Datos compartimentados por analista · CNPP Arts. 369-374
        </div>
      </div>}

      {/* Modal nuevo dispositivo */}
      {modalNuevo && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,.7)',
          zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'
        }}>
          <div style={{ background:'#0b132b', border:'1px solid #1c2541',
                        borderRadius:12, padding:28, width:420 }}>
            <h2 style={{ color:'#fbbf24', marginBottom:20, fontSize:16 }}>
              🎯 Registrar Dispositivo Objetivo
            </h2>
            <div style={{
              background:'#450a0a', border:'1px solid #f87171',
              borderRadius:6, padding:'8px 12px', marginBottom:16, fontSize:12, color:'#f87171'
            }}>
              ⚠️ Esta acción requiere fundamento legal bajo CNPP Arts. 369-374.
              Se registrará cadena de custodia digital con SHA-256.
            </div>

            {[
              { id:'caso_ref',     label:'Carpeta Judicial / NUC', ph:'NUC-XXX-2026' },
              { id:'nombre_clave', label:'Nombre Clave del Objetivo', ph:'OBJETIVO-ALFA' },
              { id:'imei',         label:'IMEI del Dispositivo', ph:'862892059193524' },
              { id:'notas',        label:'Notas (uso interno)', ph:'...' },
            ].map(f => (
              <div key={f.id} style={{ marginBottom:12 }}>
                <label>{f.label}</label>
                <input value={formDev[f.id]}
                  onChange={e => setFormDev(p => ({...p, [f.id]: e.target.value}))}
                  placeholder={f.ph} />
              </div>
            ))}

            {error && <p style={{ color:'#f87171', fontSize:12, marginBottom:12 }}>{error}</p>}

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => { setModalNuevo(false); setError(''); }}>
                Cancelar
              </button>
              <button onClick={guardarDispositivo} disabled={guardando} style={{
                padding:'8px 16px', border:'none', borderRadius:6, cursor:'pointer',
                fontSize:13, fontWeight:600, background:'#fbbf24', color:'#04080f'
              }}>
                {guardando ? 'Registrando...' : 'Confirmar y Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal cadena de custodia */}
      {custodia && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,.7)',
          zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'
        }}>
          <div style={{ background:'#0b132b', border:'1px solid #1c2541',
                        borderRadius:12, padding:28, width:500, maxHeight:'80vh', overflow:'auto' }}>
            <h2 style={{ color:'#00c8ff', marginBottom:16, fontSize:16 }}>
              🔐 Reporte de Custodia Digital
            </h2>
            <div style={{
              background: custodia.integridad === 'VÁLIDA' ? '#052e16' : '#450a0a',
              border: `1px solid ${custodia.integridad === 'VÁLIDA' ? '#4ade80' : '#f87171'}`,
              borderRadius:8, padding:12, marginBottom:16, textAlign:'center'
            }}>
              <div style={{
                fontSize:24,
                color: custodia.integridad === 'VÁLIDA' ? '#4ade80' : '#f87171'
              }}>
                {custodia.integridad === 'VÁLIDA' ? '✅' : '❌'}
              </div>
              <div style={{
                fontWeight:700, fontSize:14,
                color: custodia.integridad === 'VÁLIDA' ? '#4ade80' : '#f87171'
              }}>
                INTEGRIDAD {custodia.integridad}
              </div>
              <div style={{ color:'#8892a4', fontSize:12, marginTop:4 }}>
                {custodia.total_posiciones} registros verificados · SHA-256 encadenado
              </div>
            </div>

            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <tbody>
                {[
                  ['Dispositivo', custodia.dispositivo?.nombre_clave],
                  ['Caso', custodia.dispositivo?.caso_ref],
                  ['Total registros', custodia.total_posiciones],
                  ['Verificado en', custodia.verificado_en?.replace('T',' ').substring(0,19)],
                  ['Fundamento legal', 'CNPP Arts. 369-374'],
                ].map(([k, v]) => (
                  <tr key={k}>
                    <td style={{ color:'#8892a4', padding:'4px 0', paddingRight:16 }}>{k}</td>
                    <td style={{ color:'#e0e1dd' }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {custodia.errores?.length > 0 && (
              <div style={{ marginTop:16, background:'#450a0a', borderRadius:6, padding:10 }}>
                <div style={{ color:'#f87171', fontWeight:700, marginBottom:6 }}>
                  ⚠️ {custodia.errores.length} posición(es) con inconsistencia:
                </div>
                {custodia.errores.map((e, i) => (
                  <div key={i} style={{ fontSize:11, color:'#e0e1dd' }}>
                    ID #{e.posicion_id} · {e.timestamp} · {e.error}
                  </div>
                ))}
              </div>
            )}

            <div style={{ textAlign:'right', marginTop:20 }}>
              <button className="btn btn-ghost" onClick={() => setCustodia(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
